import { tetriOf, tetriToDecimalString } from '@/utils/money';
import type {
    JournalEntry,
    JournalEntrySourceWire,
    JournalEntryStatus,
    JournalEntrySummary,
    JournalLine,
    ValidationIssue,
    ValidationState,
} from '@/api/models';
import type { JournalEntryWriteRequest } from './requests';

/**
 * Wire ⇄ domain mapping for the ledger journal (bos-api).
 *
 * **This is the only file that knows the server's field names.** Everything
 * downstream — the service, the query hooks, the form, the list, the drawer —
 * sees the domain shapes in `@/api/models`. Same arrangement as
 * `accounts/accountsMapping.ts`, and for the same reason: the journal client was
 * authored frontend-first, so nearly every field needed reconciling. The full
 * verified contract is in `CONTRACT.md`.
 *
 * The divergences this file absorbs, each checkable against one live response:
 *
 * | domain | wire |
 * | --- | --- |
 * | `entryNumber` (display) | `entryNumberFormatted` — `entryNumber` is an **integer** |
 * | `total` (list) | `totalCount` |
 * | `LINE_TWO_SIDED` | `LINE_BOTH_SIDES` |
 * | `LINE_MISSING_AMOUNT` | `LINE_NO_AMOUNT` |
 * | `LINE_MISSING_ACCOUNT` | `LINE_NO_ACCOUNT` |
 * | `PERIOD_CLOSED` | `PERIOD_CLOSED`, **and** `dateInClosedPeriod: boolean` |
 * | issue line scope | `lineId` (server uuid), resolved here to `lineIndex` |
 * | validation totals | *not sent* — summed here from the lines |
 *
 * ## The one that used to matter
 *
 * The server once returned **`postable: true` for an entry dated in a closed
 * period**, refusing only at `POST /{id}/post` — so a client that trusted it
 * would enable Post on an entry the ledger rejects.
 *
 * **Fixed and verified**: a closed-period draft now comes back
 * `postable: false` with `PERIOD_CLOSED` in `errors`.
 *
 * Two habits are kept anyway, and neither is redundancy for its own sake:
 *
 * 1. `isPostable` is **recomputed** from the issue list rather than copied from
 *    `postable`. The two cannot then drift, and this file stays the single
 *    place that decides what postable means.
 * 2. `dateInClosedPeriod` is folded in **only when the server did not send the
 *    code** — a fallback, not a duplicate. It costs one condition and covers
 *    the case where the flag and the code disagree, which is precisely the
 *    shape the original bug had.
 */

const STATUSES: readonly string[] = ['DRAFT', 'POSTED', 'REVERSED'];

const isStatus = (value: unknown): value is JournalEntryStatus =>
    typeof value === 'string' && STATUSES.includes(value);

/**
 * An entry's origin, kept exactly as it arrived (BOS-142).
 *
 * <p>This used to check the value against a hand-written allow-list and fall
 * back to `'MANUAL'`. The list was written before SALE, PURCHASE and
 * SUPPLIER_PAYMENT existed and was never extended, so every operational entry
 * reached the screen relabelled "Manual" — which also hid its document link
 * and its guarded reversal flow, because both branch on the source.</p>
 *
 * <p>There is no allow-list now. A string is an origin; only a non-string —
 * a number, null, a missing field — is a wire fault, and that is the one case
 * that falls back. `journalMeta.sourceLabelKey` prints an origin it has no
 * label for as its own raw value, which is the honest answer.</p>
 */
const sourceOr = (
    value: unknown,
    fallback: JournalEntrySourceWire,
): JournalEntrySourceWire =>
    typeof value === 'string' && value.length > 0 ? value : fallback;

/**
 * Tetri → the decimal echo the display layer prints, sign preserved.
 *
 * Re-exported rather than defined here: the close preview builds `JournalLine`s
 * too (see `periods/periodsMapping`) and renders them through the same line
 * table, and BOS-162 moved the arithmetic itself to `@/utils/money` so the four
 * copies that had accumulated across the mapping layer became one.
 */
export const decimalGelOf = tetriToDecimalString;

const numberOr = (value: unknown, fallback: number): number =>
    typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const stringOr = (value: unknown, fallback: string): string =>
    typeof value === 'string' ? value : fallback;

export type LineWire = {
    id?: unknown;
    accountId?: unknown;
    accountCode?: unknown;
    accountName?: unknown;
    debit?: unknown;
    debitTetri?: unknown;
    credit?: unknown;
    creditTetri?: unknown;
    memo?: unknown;
};

/**
 * Exported because a draft entry is a journal entry whichever subsidiary built
 * it: `dividendsMapping` renders preview drafts through this same mapper rather
 * than reimplementing it. A second copy loses `sideTetri`'s decimal fallback,
 * and a balanced draft then renders every line as ₾0.00.
 */
export const mapLine = (wire: LineWire): JournalLine => {
    const debitTetri = tetriOf(wire.debitTetri, wire.debit);
    const creditTetri = tetriOf(wire.creditTetri, wire.credit);
    return {
        ...(typeof wire.id === 'string' ? { id: wire.id } : {}),
        accountId: stringOr(wire.accountId, ''),
        ...(typeof wire.accountCode === 'string'
            ? { accountCode: wire.accountCode }
            : {}),
        ...(typeof wire.accountName === 'string'
            ? { accountName: wire.accountName }
            : {}),
        debit: decimalGelOf(debitTetri),
        debitTetri,
        credit: decimalGelOf(creditTetri),
        creditTetri,
        memo: stringOr(wire.memo, ''),
    };
};

/**
 * Server validation code → this client's vocabulary. An unrecognised code is
 * **not dropped**: it becomes `SERVER_REJECTED` carrying the server's own
 * message, so a rule added server-side still reaches the user with a real
 * explanation instead of silently disappearing.
 */
const CODE_MAP: Record<string, ValidationIssue['code']> = {
    UNBALANCED: 'UNBALANCED',
    TOO_FEW_LINES: 'TOO_FEW_LINES',
    LINE_BOTH_SIDES: 'LINE_TWO_SIDED',
    LINE_NO_AMOUNT: 'LINE_MISSING_AMOUNT',
    LINE_NO_ACCOUNT: 'LINE_MISSING_ACCOUNT',
    PERIOD_CLOSED: 'PERIOD_CLOSED',
};

type ValidationErrorWire = {
    code?: unknown;
    message?: unknown;
    lineId?: unknown;
};

type ValidationStateWire = {
    postable?: unknown;
    balanced?: unknown;
    errors?: unknown;
};

const LINE_SCOPED: readonly ValidationIssue['code'][] = [
    'LINE_MISSING_ACCOUNT',
    'LINE_MISSING_AMOUNT',
    'LINE_TWO_SIDED',
];

const mapIssue = (
    wire: ValidationErrorWire,
    lineIndexById: ReadonlyMap<string, number>,
): ValidationIssue | null => {
    const rawCode = typeof wire.code === 'string' ? wire.code : '';
    const message = typeof wire.message === 'string' ? wire.message : '';
    const code = CODE_MAP[rawCode];

    if (!code) {
        // No message and no known code means nothing showable — drop it rather
        // than render an empty error row.
        return message ? { code: 'SERVER_REJECTED', message } : null;
    }

    if (LINE_SCOPED.includes(code)) {
        const lineId = typeof wire.lineId === 'string' ? wire.lineId : '';
        const lineIndex = lineIndexById.get(lineId);
        if (lineIndex === undefined) {
            // A line-scoped error whose row we cannot identify is still real —
            // report it against the entry rather than pinning it to row 0.
            return message ? { code: 'SERVER_REJECTED', message } : null;
        }
        return { code, lineIndex, message } as ValidationIssue;
    }

    return { code, message } as ValidationIssue;
};

/**
 * Build the domain validation state from the server's, the entry's lines, and
 * its closed-period flag.
 *
 * Two things the server does not give us and this must:
 *
 * 1. **Totals.** `EntryValidationState` carries only `postable`/`balanced`/
 *    `errors`. The debit and credit totals are summed here from the lines,
 *    which do carry tetri.
 * 2. **The period rule.** See the note at the top of this file — the server's
 *    `postable` ignores it, so `isPostable` is recomputed from the issue list
 *    rather than copied.
 */
export const mapValidationState = (
    wire: ValidationStateWire | undefined,
    lines: readonly JournalLine[],
    dateInClosedPeriod: boolean,
): ValidationState => {
    const lineIndexById = new Map<string, number>();
    lines.forEach((line, index) => {
        if (line.id) {
            lineIndexById.set(line.id, index);
        }
    });

    const rawErrors = Array.isArray(wire?.errors) ? wire.errors : [];
    const issues = rawErrors
        .map((error) => mapIssue(error as ValidationErrorWire, lineIndexById))
        .filter((issue): issue is ValidationIssue => issue !== null);

    // Fallback, not a duplicate: the server sends PERIOD_CLOSED as a validation
    // code and this flag says the same thing, so the fold only fires when the
    // code is absent. Reporting both would show the user one reason twice.
    if (dateInClosedPeriod && !issues.some((i) => i.code === 'PERIOD_CLOSED')) {
        issues.push({ code: 'PERIOD_CLOSED' });
    }

    const totalDebitTetri = lines.reduce((sum, l) => sum + l.debitTetri, 0);
    const totalCreditTetri = lines.reduce((sum, l) => sum + l.creditTetri, 0);

    return {
        isPostable: issues.length === 0,
        issues,
        totalDebitTetri,
        totalCreditTetri,
        differenceTetri: totalDebitTetri - totalCreditTetri,
    };
};

type EntryWire = {
    id?: unknown;
    entryNumberFormatted?: unknown;
    date?: unknown;
    description?: unknown;
    documentRef?: unknown;
    status?: unknown;
    source?: unknown;
    etag?: unknown;
    dateInClosedPeriod?: unknown;
    reversesEntryId?: unknown;
    reversedByEntryId?: unknown;
    postedAt?: unknown;
    createdAt?: unknown;
    validationState?: unknown;
    lines?: unknown;
};

export const mapJournalEntry = (raw: unknown): JournalEntry => {
    const wire = (raw ?? {}) as EntryWire;
    const lines = (Array.isArray(wire.lines) ? wire.lines : []).map((line) =>
        mapLine(line as LineWire),
    );
    const dateInClosedPeriod = wire.dateInClosedPeriod === true;

    return {
        id: stringOr(wire.id, ''),
        entryNumber:
            typeof wire.entryNumberFormatted === 'string'
                ? wire.entryNumberFormatted
                : null,
        date: stringOr(wire.date, ''),
        description: stringOr(wire.description, ''),
        documentRef: stringOr(wire.documentRef, ''),
        status: isStatus(wire.status) ? wire.status : 'DRAFT',
        ...(typeof wire.source === 'string' && wire.source.length > 0
            ? { source: wire.source }
            : {}),
        lines,
        validationState: mapValidationState(
            wire.validationState as ValidationStateWire | undefined,
            lines,
            dateInClosedPeriod,
        ),
        etag: stringOr(wire.etag, ''),
        dateInClosedPeriod,
        reversesEntryId:
            typeof wire.reversesEntryId === 'string'
                ? wire.reversesEntryId
                : null,
        reversedByEntryId:
            typeof wire.reversedByEntryId === 'string'
                ? wire.reversedByEntryId
                : null,
        postedAt: typeof wire.postedAt === 'string' ? wire.postedAt : null,
        ...(typeof wire.createdAt === 'string'
            ? { createdAt: wire.createdAt }
            : {}),
    };
};

type SummaryWire = {
    id?: unknown;
    entryNumberFormatted?: unknown;
    date?: unknown;
    description?: unknown;
    documentRef?: unknown;
    status?: unknown;
    source?: unknown;
    amount?: unknown;
    amountTetri?: unknown;
    lineCount?: unknown;
    etag?: unknown;
};

export const mapJournalEntrySummary = (raw: unknown): JournalEntrySummary => {
    const wire = (raw ?? {}) as SummaryWire;
    const entryNumber =
        typeof wire.entryNumberFormatted === 'string'
            ? wire.entryNumberFormatted
            : null;
    return {
        id: stringOr(wire.id, ''),
        entryNumber,
        date: stringOr(wire.date, ''),
        description: stringOr(wire.description, ''),
        documentRef: stringOr(wire.documentRef, ''),
        status: isStatus(wire.status) ? wire.status : 'DRAFT',
        source: sourceOr(wire.source, 'MANUAL'),
        amountTetri: tetriOf(wire.amountTetri, wire.amount),
        lineCount: Math.trunc(numberOr(wire.lineCount, 0)),
        etag: stringOr(wire.etag, ''),
        // A row with no number has not been through a post — the list renders
        // that as pending rather than as a numbered entry with a blank number.
        pendingSync: entryNumber === null,
    };
};

type ListWire = {
    items?: unknown;
    page?: unknown;
    pageSize?: unknown;
    totalCount?: unknown;
};

/** `totalCount` → `total`; `totalPages` is derivable and not carried. */
export const mapJournalEntryList = (raw: unknown) => {
    const wire = (raw ?? {}) as ListWire;
    const items = (Array.isArray(wire.items) ? wire.items : []).map(
        mapJournalEntrySummary,
    );
    return {
        items,
        total: Math.trunc(numberOr(wire.totalCount, items.length)),
        page: Math.trunc(numberOr(wire.page, 1)),
        pageSize: Math.trunc(numberOr(wire.pageSize, items.length)),
    };
};

/**
 * A line as the write edge carries it — **integer tetri only**.
 *
 * `JournalLineInput` also accepts `debit`/`credit` as doubles, and resolves the
 * two forms against each other server-side (`resolvedDebitTetri`, plus a
 * `hasConflict` flag and a `400 AMOUNT_CONFLICT` when they disagree). Sending
 * only the integer keeps that resolution trivial and the conflict unreachable:
 * there is nothing for it to disagree with.
 *
 * The amounts arrive here already integral — `toWriteRequest` did the parse.
 * Nothing in this file touches user-typed text.
 */
type JournalLineInputWire = {
    accountId: string | null;
    debitTetri: number;
    creditTetri: number;
    memo: string;
};

const toLineWire = (line: {
    accountId: string | null;
    debitTetri: number;
    creditTetri: number;
    memo: string;
}): JournalLineInputWire => ({
    accountId: line.accountId,
    debitTetri: line.debitTetri,
    creditTetri: line.creditTetri,
    memo: line.memo,
});

/**
 * `POST /ledger/journal-entries` body. Carries the client-generated `id` —
 * that is what makes create idempotent, so a retried create returns 200 with
 * the same entry instead of a duplicate.
 *
 * **Never carries `source`.** The server rejects a client-supplied one with
 * `400 SOURCE_READONLY`, and there is no field on `JournalEntryWriteRequest`
 * that could carry it in the first place.
 */
export const toCreateBody = (body: JournalEntryWriteRequest) => ({
    id: body.id,
    date: body.date,
    description: body.description,
    documentRef: body.documentRef,
    lines: body.lines.map(toLineWire),
});

/**
 * `PUT /ledger/journal-entries/{id}` body. No `id` (it is in the path) and
 * **the etag is required here even though the server treats it as optional** —
 * omitting it makes the write unguarded, and a silent last-writer-wins on a
 * ledger draft is not a trade this client makes.
 */
export const toUpdateBody = (body: JournalEntryWriteRequest, etag: string) => ({
    date: body.date,
    description: body.description,
    documentRef: body.documentRef,
    lines: body.lines.map(toLineWire),
    etag,
});
